/**
 * Whether we may count her pregnancy in weeks yet.
 *
 * A positive beta is a result. It is not a pregnancy she has announced, and the
 * difference is the whole of this module. Between the two sits a stretch that
 * belongs to her: a first positive is often followed by a repeat, an early loss
 * is common and rarely spoken of on the day it happens, and plenty of people do
 * not consider themselves pregnant the afternoon a number came back. Telling
 * someone in that stretch that she is "6 weeks and 3 days along" answers a
 * question she has not asked, in a vocabulary she may not have chosen — and if
 * it ends, the app spent those days counting.
 *
 * So gestational age is gated on her word, never on the record alone. She says
 * so by opting into pregnancy follow-up (`status: "opted_in"` on
 * `/cycles/{cycleId}/pregnancy-follow-up`) or by telling us where the pregnancy
 * stands. Nothing else counts: not a positive result, not an acknowledged
 * outcome, not a clinic-verified beta.
 *
 * Pure and testable on purpose. This decides whether a number appears next to a
 * patient's pregnancy, and a rule like that should be assertable without
 * rendering anything.
 */

/** The follow-up record's status, as the API returns it. */
export type PregnancyFollowUpStatus =
  "not_configured" | "dismissed" | "opted_in" | "ended" | "unresolved";

/** What she last told us about where the pregnancy stands, if anything. */
export type PregnancyStands = "continuing" | "ended" | "birth";

export type PregnancyAnnouncement = {
  followUpStatus?: PregnancyFollowUpStatus | null;
  pregnancyStands?: PregnancyStands | null;
};

/**
 * Whether she has told us there is a pregnancy — in either of the two ways the
 * product gives her.
 *
 * `dismissed` is an answer too, and the answer is no. `unresolved` and
 * `not_configured` are the absence of one.
 */
export function hasAnnouncedPregnancy(input: PregnancyAnnouncement): boolean {
  if (input.pregnancyStands === "continuing") return true;
  return input.followUpStatus === "opted_in";
}

/**
 * Whether the pregnancy she announced is still ongoing.
 *
 * A birth and a loss both end the stretch weeks are counted for, and either can
 * be recorded without the original announcement changing.
 */
export function announcedPregnancyIsOngoing(
  input: PregnancyAnnouncement,
): boolean {
  if (!hasAnnouncedPregnancy(input)) return false;
  if (input.pregnancyStands === "ended" || input.pregnancyStands === "birth") {
    return false;
  }
  return input.followUpStatus !== "ended";
}

/**
 * Whether a gestational age or a week-dated milestone may be shown.
 *
 * Both halves are required and neither implies the other: she has to have
 * announced an ongoing pregnancy, *and* the dating has to exist. The API returns
 * `gestation: null` when it has no anchor to count from, and a week count
 * invented from a missing anchor would be worse than none — it would be a number
 * she could act on.
 */
export function mayShowGestationalAge(input: {
  announcement: PregnancyAnnouncement;
  /** The API's `gestation` block, or null when it cannot date the pregnancy. */
  gestation: unknown | null | undefined;
}): boolean {
  if (!input.gestation) return false;
  return announcedPregnancyIsOngoing(input.announcement);
}
